import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FileText, Search, Loader2, Play, ArrowLeft, Tags
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

export default function SelectTemplatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'case_specific', 'generic'
  const [runningTemplate, setRunningTemplate] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [searchQuery, filterType, templates]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUser(await response.json());
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(t => {
        if (filterType === 'case_specific') return t.is_case_specific;
        if (filterType === 'generic') return !t.is_case_specific;
        return true;
      });
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        (t.description && t.description.toLowerCase().includes(query))
      );
    }
    
    setFilteredTemplates(filtered);
  };

  const handleRunTemplate = async (templateId) => {
    if (!caseId) {
      toast.error('Please select a case first');
      navigate('/cases');
      return;
    }

    setRunningTemplate(templateId);
    
    try {
      // Navigate to template run page with case context
      navigate(`/templates/${templateId}/run?caseId=${caseId}`);
    } catch (error) {
      toast.error('Failed to load template');
    } finally {
      setRunningTemplate(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8 lg:pt-8 pt-20">
            
            {/* Header */}
            <div className="mb-6">
              {caseId && (
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/cases/${caseId}`)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Case
                </Button>
              )}
              <h1 className="text-3xl font-bold text-slate-900">Select Template</h1>
              <p className="text-slate-500 mt-1">Choose a template to generate a document for your case</p>
            </div>

            {/* Search and Filter */}
            <div className="mb-6 flex gap-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="pl-10"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm"
              >
                <option value="all">All Templates</option>
                <option value="case_specific">Case-Specific</option>
                <option value="generic">Generic</option>
              </select>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No templates found</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredTemplates.map(template => (
                  <div 
                    key={template.template_id}
                    className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-violet-200 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-violet-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{template.name}</h3>
                          {template.is_case_specific && (
                            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                              Case-Specific
                            </span>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-sm text-slate-600 mb-2">{template.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                          <span>{template.total_fields || 0} fields</span>
                          {template.category_name && (
                            <span className="flex items-center gap-1">
                              <Tags className="w-3 h-3" />
                              {template.category_name}
                            </span>
                          )}
                        </div>
                      </div>

                      <Button
                        size="sm"
                        onClick={() => handleRunTemplate(template.template_id)}
                        disabled={!caseId || runningTemplate === template.template_id}
                        className="rounded-lg bg-violet-600 hover:bg-violet-700"
                      >
                        {runningTemplate === template.template_id ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                        ) : (
                          <><Play className="w-4 h-4 mr-2" /> Use Template</>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
